import type { ModerationEntity } from '../model/schema';
import { entityBag, isLocalizedText } from './entity-bag';
import { resolveEntityName } from './names';

export interface EntityAddress {
  line: string;
  coords: string | undefined;
}

/** Restaurant address: localized street line + `lat, lng` when present. */
export function entityAddress(entity: ModerationEntity): EntityAddress | undefined {
  const value: unknown = entityBag(entity).address;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const bag = value as Record<string, unknown>;
  const line = isLocalizedText(bag.line) ? resolveEntityName(bag.line, '') : '';
  const lat = typeof bag.latitude === 'number' ? bag.latitude : undefined;
  const lng = typeof bag.longitude === 'number' ? bag.longitude : undefined;
  if (line === '' && lat === undefined) return undefined;
  return {
    line,
    coords: lat !== undefined && lng !== undefined ? `${lat}, ${lng}` : undefined,
  };
}

/** `working_hours: {open_time, close_time}` → `08:00 – 22:00`. */
export function entityWorkingHours(entity: ModerationEntity): string | undefined {
  const range = entityWorkingRange(entity);
  if (!range) return undefined;
  if (range.open !== '' && range.close !== '') return `${range.open} – ${range.close}`;
  return range.open !== '' ? range.open : range.close;
}

export interface WorkingRange {
  open: string;
  close: string;
}

/** Working-hours range as separate open/close lines (day-schedule pills). */
export function entityWorkingRange(entity: ModerationEntity): WorkingRange | undefined {
  const value: unknown = entityBag(entity).working_hours;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const bag = value as Record<string, unknown>;
  const open = typeof bag.open_time === 'string' ? bag.open_time.trim() : '';
  const close = typeof bag.close_time === 'string' ? bag.close_time.trim() : '';
  if (open === '' && close === '') return undefined;
  return { open, close };
}

export interface EntityCoordinates {
  lat: number;
  lng: number;
}

/** Numeric address coordinates for the location map (0/0 reads as unset). */
export function entityCoordinates(
  entity: ModerationEntity,
): EntityCoordinates | undefined {
  const value: unknown = entityBag(entity).address;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const bag = value as Record<string, unknown>;
  const lat = typeof bag.latitude === 'number' ? bag.latitude : undefined;
  const lng = typeof bag.longitude === 'number' ? bag.longitude : undefined;
  if (lat === undefined || lng === undefined || (lat === 0 && lng === 0)) {
    return undefined;
  }
  return { lat, lng };
}
